import { useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { SignerContactType } from '@dealstack/shared/lib/generated';
import {
	EidEasySignStatus,
	type OtpResendOutcome,
} from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import { maskContact } from '@dealstack/shared/lib/common/maskContact';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	makeEidEasyClient,
	dispatchOtpCode,
	validateOtpCode,
	classifyOtpSubmitFailure,
	parseOtpRateLimitSeconds,
	toErrorKey,
	isUserInitiatedError,
} from './useEidEasySign.utils';
import { makeTransportRequest } from './bulkSignTransport';
import { showBulkSignSkipNotices } from './bulkSignSkipNotices';
import type {
	BulkSignTransport,
	SignRequestFields,
	BulkSignRequestParams,
} from './bulkSignTransport';

interface EidEasyBulkPrepareResponse {
	payload: {
		docId: string;
		clientId: string;
		baseUrl: string;
		mockMode?: boolean;
		skippedInstanceIds?: string[];
		witnessSkippedInstanceIds?: string[];
		deferredInstanceIds?: string[];
	};
}

interface UseBulkOtpSignOptions {
	transport: BulkSignTransport;
	sentryFlowTag: string;
	onStatusChange: (status: EidEasySignStatus) => void;
	onError: (error: string) => void;
	onComplete: () => void;
	onVerifyingChange: (verifying: boolean) => void;
	onCodeError: (errorKey: string | null) => void;
}

export interface BulkOtpContext {
	channel: SignerContactType;
	maskedContact: string;
	docId: string;
	clientId: string;
	baseUrl: string;
	mockMode: boolean;
}

/**
 * Bulk OTP signing.
 *
 * One OTP code authenticates the whole batch. The eID Easy `signer` payload is
 * set once per prepare call (not per file), so a single SMS/email dispatch lets
 * the user sign every prepared file with one code submission. Mirrors the
 * single-doc OTP two-call SDK flow (start-signing dispatches → user enters →
 * SDK validates → backend bulk-complete downloads and saves all signed PDFs).
 */
export const useBulkOtpSign = ({
	transport,
	sentryFlowTag,
	onStatusChange,
	onError,
	onComplete,
	onVerifyingChange,
	onCodeError,
}: UseBulkOtpSignOptions) => {
	const tMipManage = useTranslationMipManage();
	const otpContextRef = useRef<BulkOtpContext | null>(null);
	const attemptsRef = useRef(0);
	const resendingRef = useRef(false);
	const verifyingRef = useRef(false);

	const request = useCallback(
		<T>(params: BulkSignRequestParams) => makeTransportRequest<T>(transport, params),
		[transport]
	);

	const buildClient = useCallback((ctx: BulkOtpContext) => makeEidEasyClient(ctx), []);

	const confirmOtp = useCallback(
		async (
			signRequestFields: SignRequestFields,
			drawnSignatureBase64: string,
			channel: SignerContactType,
			value: string
		) => {
			onStatusChange(EidEasySignStatus.PREPARING);
			onError('');
			onCodeError(null);

			try {
				const prepareResult = await request<EidEasyBulkPrepareResponse>({
					endpoint: transport.buildEndpoint('prepare'),
					method: HTTP_METHOD.POST,
					body: JSON.stringify({
						...signRequestFields,
						method: EidEasySigningMethod.OTP,
						drawnSignatureBase64,
						otpContact: { channel, value },
					}),
				});

				const { docId, clientId, baseUrl, mockMode } = prepareResult.payload;
				showBulkSignSkipNotices(prepareResult.payload, tMipManage);
				const ctx: BulkOtpContext = {
					channel,
					maskedContact: maskContact(value, channel),
					docId,
					clientId,
					baseUrl,
					mockMode: mockMode ?? false,
				};
				otpContextRef.current = ctx;
				attemptsRef.current = 0;

				if (!ctx.mockMode) {
					await dispatchOtpCode(buildClient(ctx));
				}
				onStatusChange(EidEasySignStatus.OTP_CODE_ENTRY);
			} catch (err: unknown) {
				if (!isUserInitiatedError(err)) {
					Sentry.captureException(err, {
						tags: { signingFlow: sentryFlowTag, phase: 'prepare' },
					});
				}
				onError(toErrorKey(err));
				onStatusChange(EidEasySignStatus.ERROR);
			}
		},
		[
			transport,
			sentryFlowTag,
			onStatusChange,
			onError,
			onCodeError,
			buildClient,
			request,
			tMipManage,
		]
	);

	const submitOtpCode = useCallback(
		async (code: string) => {
			const ctx = otpContextRef.current;
			if (!ctx || verifyingRef.current) {
				return;
			}
			verifyingRef.current = true;
			onVerifyingChange(true);
			onCodeError(null);

			try {
				// Phase 1: validate the OTP code via the SDK (skipped in mockMode).
				// Only validation failures feed classifyOtpSubmitFailure.
				if (!ctx.mockMode) {
					try {
						await validateOtpCode(buildClient(ctx), ctx.channel, code);
					} catch (validationErr: unknown) {
						if (!isUserInitiatedError(validationErr)) {
							Sentry.captureException(validationErr, {
								tags: { signingFlow: sentryFlowTag, phase: 'submit-code' },
							});
						}
						const { fatal, errorKey } = classifyOtpSubmitFailure(
							validationErr,
							attemptsRef.current
						);
						if (fatal) {
							onError(errorKey);
							onStatusChange(EidEasySignStatus.ERROR);
						} else {
							attemptsRef.current += 1;
							onCodeError(errorKey);
						}
						return;
					}
				}

				// Phase 2: validation passed (or mockMode). Complete the bulk signing.
				// Any failure here is always fatal — the code was correct.
				onStatusChange(EidEasySignStatus.COMPLETING);
				try {
					await request({
						endpoint: transport.buildEndpoint('complete'),
						method: HTTP_METHOD.POST,
						body: JSON.stringify({ docId: ctx.docId }),
					});
					otpContextRef.current = null;
					onStatusChange(EidEasySignStatus.DONE);
					onComplete();
				} catch (completionErr: unknown) {
					if (!isUserInitiatedError(completionErr)) {
						Sentry.captureException(completionErr, {
							tags: { signingFlow: sentryFlowTag, phase: 'bulk-complete' },
						});
					}
					onError(toErrorKey(completionErr));
					onStatusChange(EidEasySignStatus.ERROR);
				}
			} finally {
				verifyingRef.current = false;
				onVerifyingChange(false);
			}
		},
		[
			transport,
			sentryFlowTag,
			onStatusChange,
			onError,
			onComplete,
			onVerifyingChange,
			onCodeError,
			buildClient,
			request,
		]
	);

	const resendOtpCode = useCallback(async (): Promise<OtpResendOutcome> => {
		const ctx = otpContextRef.current;
		if (!ctx || resendingRef.current) {
			return { status: 'failed' };
		}
		resendingRef.current = true;
		onCodeError(null);
		try {
			if (!ctx.mockMode) {
				await dispatchOtpCode(buildClient(ctx));
			}
			attemptsRef.current = 0;
			return { status: 'sent' };
		} catch (err: unknown) {
			// eID Easy's "wait N seconds" resend cooldown is not fatal — surface it
			// so the entry screen re-arms its countdown instead of erroring out.
			const waitSeconds = parseOtpRateLimitSeconds(err);
			if (waitSeconds !== null) {
				return { status: 'rate_limited', waitSeconds };
			}
			if (!isUserInitiatedError(err)) {
				Sentry.captureException(err, {
					tags: { signingFlow: sentryFlowTag, phase: 'resend' },
				});
			}
			onError(toErrorKey(err));
			onStatusChange(EidEasySignStatus.ERROR);
			return { status: 'failed' };
		} finally {
			resendingRef.current = false;
		}
	}, [sentryFlowTag, onCodeError, onError, onStatusChange, buildClient]);

	const reset = useCallback(() => {
		otpContextRef.current = null;
		attemptsRef.current = 0;
	}, []);

	const getOtpContext = useCallback((): BulkOtpContext | null => otpContextRef.current, []);

	return {
		confirmOtp,
		submitOtpCode,
		resendOtpCode,
		reset,
		getOtpContext,
	};
};
